import { FormEvent, useMemo, useRef, useState } from 'react';
import { FiMessageCircle, FiMinus, FiSend, FiX } from 'react-icons/fi';
import { usePortalData } from '../../hooks/usePortalData';
import type { Program } from '../../types';
import { groupByUniversity, normalizeSearch, placementCopy, safe, scholarshipCopy } from '../../utils/text';

type Message = {
  role: 'user' | 'bot';
  text: string;
};

const starters = ['Amity University', 'MBA under 2 lakh', 'Compare Amity and Manipal', 'Scholarship details', 'Placement support'];

export function Chatbot() {
  const { data } = usePortalData();
  const programs = data?.programs || [];
  const grouped = useMemo(() => groupByUniversity(programs), [programs]);
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'bot',
      text: 'Hello, I am Ask Koursepedia. Ask me about universities, programs, fees, eligibility, scholarships, placements, brochures, or admissions.',
    },
  ]);
  const endRef = useRef<HTMLDivElement>(null);

  function submit(value: string) {
    const query = value.trim();
    if (!query) return;
    const answer = answerQuery(query, programs, grouped);
    setMessages((items) => [...items, { role: 'user', text: query }, { role: 'bot', text: answer }]);
    setInput('');
    setOpen(true);
    setMinimized(false);
    requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }));
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    submit(input);
  }

  return (
    <section className="fixed bottom-4 right-4 z-50">
      {open && !minimized ? (
        <div className="mb-3 flex h-[min(680px,calc(100vh-120px))] w-[min(420px,calc(100vw-32px))] flex-col overflow-hidden rounded-[2rem] border border-white bg-white shadow-premium">
          <header className="flex items-center justify-between bg-ink p-4 text-white">
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand-600 font-display font-extrabold">KP</span>
              <div>
                <strong className="block">Ask Koursepedia</strong>
                <span className="text-xs text-white/65">University Assistant</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button className="rounded-xl p-2 hover:bg-white/10" type="button" aria-label="Minimize Ask Koursepedia" onClick={() => setMinimized(true)}>
                <FiMinus />
              </button>
              <button className="rounded-xl p-2 hover:bg-white/10" type="button" aria-label="Close Ask Koursepedia" onClick={() => setOpen(false)}>
                <FiX />
              </button>
            </div>
          </header>
          <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-4">
            {messages.map((message, index) => (
              <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <p
                  className={`max-w-[86%] whitespace-pre-line rounded-3xl px-4 py-3 text-sm leading-6 ${
                    message.role === 'user' ? 'bg-brand-600 text-white' : 'bg-white text-slate-700 shadow-sm'
                  }`}
                >
                  {message.text}
                </p>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="border-t border-slate-100 bg-white p-3">
            <div className="mb-3 flex gap-2 overflow-x-auto">
              {starters.map((starter) => (
                <button
                  key={starter}
                  type="button"
                  onClick={() => submit(starter)}
                  className="shrink-0 rounded-full bg-brand-50 px-3 py-2 text-xs font-bold text-brand-700"
                >
                  {starter}
                </button>
              ))}
            </div>
            <form className="flex gap-2" onSubmit={onSubmit}>
              <input
                className="input"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="Ask about programs, fees, eligibility..."
                type="search"
              />
              <button className="btn-primary !min-h-12 !px-4" type="submit" aria-label="Send message">
                <FiSend />
              </button>
            </form>
            <p className="mt-2 text-xs text-slate-400">Searches current portal data. Verify final terms before enrollment.</p>
          </div>
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => {
          setOpen((value) => !value || minimized);
          setMinimized(false);
        }}
        className="flex items-center gap-3 rounded-3xl bg-ink px-4 py-3 text-left text-white shadow-premium transition hover:-translate-y-0.5"
        aria-expanded={open && !minimized}
        aria-label="Open Ask Koursepedia"
      >
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand-600">
          <FiMessageCircle />
        </span>
        <span>
          <strong className="block text-sm">Ask Koursepedia</strong>
          <small className="text-xs text-white/65">University assistant</small>
        </span>
      </button>
    </section>
  );
}

function answerQuery(query: string, programs: Program[], grouped: Record<string, Program[]>) {
  if (!programs.length) return 'University data is still loading. Please try again in a moment.';
  const normalized = normalizeSearch(query);
  if (/\bcompare\b/.test(normalized)) return compareAnswer(normalized, grouped);
  if (/\badmission|apply|documents?\b/.test(normalized)) {
    return 'Admission process:\n1. Check eligibility for the selected program.\n2. Complete the application on the official university portal.\n3. Upload marksheets, ID proof, photograph, and required certificates.\n4. Pay the applicable fee after university verification.';
  }

  const university = Object.keys(grouped).find((name) => {
    const n = normalizeSearch(name);
    return n === normalized || normalized.includes(n) || n.split(' ').some((token) => token.length > 3 && normalized.includes(token));
  });
  const scope = university ? grouped[university] : programs;
  const budget = extractBudget(normalized);
  const intent = detectIntent(normalized);
  const matches = scope
    .filter((program) => !budget || (Number.isFinite(program.feeValue || NaN) && (program.feeValue || Infinity) <= budget))
    .filter((program) => score(program, normalized, intent) > 0)
    .sort((a, b) => score(b, normalized, intent) - score(a, normalized, intent))
    .slice(0, 5);

  const selected = matches.length ? matches : scope.slice(0, 5);
  if (!selected.length) return 'I could not find a confident match in the current portal data. Try a university, program, specialization, fee, placement, or eligibility question.';

  if (intent === 'fee') return selected.map((program) => `${program.university} · ${program.program}: ${safe(program.fee)}${program.emi ? `, EMI: ${program.emi}` : ''}`).join('\n');
  if (intent === 'placement') return selected.map((program) => `${program.university} · ${program.program}: ${placementCopy(program)}`).join('\n');
  if (intent === 'scholarship') return selected.map((program) => `${program.university} · ${program.program}: ${scholarshipCopy(program)}`).join('\n');
  if (intent === 'eligibility') return selected.map((program) => `${program.university} · ${program.program}: ${safe(program.eligibility)}`).join('\n');
  if (intent === 'recognition') return selected.map((program) => `${program.university}: ${safe(program.accreditation)}`).join('\n');

  return selected
    .map(
      (program) =>
        `${program.university} · ${program.program}\nFee: ${safe(program.fee)}\nDuration: ${safe(program.duration)}\nEligibility: ${safe(program.eligibility)}\nPlacement: ${placementCopy(program)}`,
    )
    .join('\n\n');
}

function detectIntent(query: string) {
  if (/\bfee|fees|cost|price|emi|budget|under\b/.test(query)) return 'fee';
  if (/\bplacement|career|job\b/.test(query)) return 'placement';
  if (/\bscholarship|waiver|discount\b/.test(query)) return 'scholarship';
  if (/\beligibility|eligible|qualification\b/.test(query)) return 'eligibility';
  if (/\bapproval|ugc|naac|recognition|valid|validity|aicte\b/.test(query)) return 'recognition';
  return 'general';
}

function score(program: Program, query: string, intent: string) {
  const text = normalizeSearch(Object.values(program).join(' '));
  let value = 0;
  query.split(' ').forEach((token) => {
    if (token.length > 2 && text.includes(token)) value += 2;
  });
  if (intent !== 'general') value += 1;
  return value;
}

function extractBudget(query: string) {
  if (!/\b(under|below|max|maximum|less than)\b/.test(query)) return null;
  const match = query.match(/(\d+(?:\.\d+)?)\s*(lakh|lac|k|thousand)?/);
  if (!match) return null;
  const value = Number(match[1]);
  if (!Number.isFinite(value)) return null;
  if (/lakh|lac/.test(match[2] || '')) return value * 100000;
  if (/k|thousand/.test(match[2] || '')) return value * 1000;
  return value;
}

function compareAnswer(query: string, grouped: Record<string, Program[]>) {
  const universities = Object.keys(grouped)
    .map((name) => ({
      name,
      score: normalizeSearch(name)
        .split(' ')
        .filter((token) => token.length > 3 && query.includes(token)).length,
    }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 2)
    .map((item) => item.name);
  if (universities.length < 2) return 'Tell me two university names to compare, for example: Compare Amity and Manipal.';
  return universities
    .map((name) => {
      const items = grouped[name];
      const fees = items.map((program) => program.feeValue).filter(Number.isFinite) as number[];
      return `${name}: ${items.length} programs, fee starts ${fees.length ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Math.min(...fees)) : 'not listed'}, recognition: ${safe(items[0]?.accreditation)}`;
    })
    .join('\n');
}
